/**
 * Scatter/bubble chart showing spend vs. ROAS per campaign using Nivo ScatterPlot.
 */

import { ResponsiveScatterPlot } from '@nivo/scatterplot'
import { useNivoTheme, useChartColors } from './nivoTheme'
import { formatMarketingCents } from '../format'

export interface ScatterPoint {
  id: string
  spend: number
  roas: number
  conversions: number
  status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED'
}

interface SpendRoasScatterProps {
  data: ScatterPoint[]
  height?: number
  highlights?: string[]
  showTrendLine?: boolean
}

function statusColors(chartColors: string[]): Record<string, string> {
  return {
    ACTIVE: chartColors[0],
    PAUSED: '#9ca3af',
    ARCHIVED: chartColors[4],
  }
}

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  ARCHIVED: 'Archived',
}

function formatCurrency(cents: number): string {
  return formatMarketingCents(cents, undefined, { fractionDigits: 0 })
}

const BreakEvenLayer = ({ yScale }: any) => {
  const y = yScale(1)
  return (
    <line
      x1={0}
      x2="100%"
      y1={y}
      y2={y}
      stroke="#ef4444"
      strokeWidth={1}
      strokeDasharray="6,4"
      strokeOpacity={0.5}
    />
  )
}

export function SpendRoasScatter({
  data,
  height = 320,
  highlights: _highlights,
  showTrendLine: _showTrendLine = false,
}: SpendRoasScatterProps) {
  const theme = useNivoTheme()
  const chartColors = useChartColors()

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        No spend/ROAS data available
      </div>
    )
  }

  // Calculate node size scale based on conversions
  const allConversions = data.map((d) => d.conversions)
  const minConversions = Math.min(...allConversions)
  const maxConversions = Math.max(...allConversions)
  const conversionRange = maxConversions - minConversions || 1

  function nodeSize(conversions: number): number {
    const normalized = (conversions - minConversions) / conversionRange
    return Math.round(8 + normalized * 24) // min 8, max 32
  }

  // Group by status for Nivo series
  const statusGroups: Record<string, ScatterPoint[]> = {}
  data.forEach((point) => {
    if (!statusGroups[point.status]) {
      statusGroups[point.status] = []
    }
    statusGroups[point.status].push(point)
  })

  const seriesData = Object.entries(statusGroups).map(([status, points]) => ({
    id: STATUS_LABELS[status] || status,
    data: points.map((p) => ({
      x: p.spend,
      y: p.roas,
      campaignName: p.id,
      conversions: p.conversions,
      status: p.status,
    })),
  }))

  // Color mapping by series label
  const statusColorMap = statusColors(chartColors)
  const seriesColorMap: Record<string, string> = {}
  Object.entries(STATUS_LABELS).forEach(([status, label]) => {
    seriesColorMap[label] = statusColorMap[status]
  })

  return (
    <div style={{ height }}>
      <ResponsiveScatterPlot
        data={seriesData}
        theme={theme}
        margin={{ top: 20, right: 20, bottom: 50, left: 70 }}
        xFormat={(v) => formatCurrency(Number(v))}
        yFormat={(v) => `${Number(v).toFixed(1)}x`}
        colors={(d) => seriesColorMap[d.serieId as string] ?? chartColors[0]}
        nodeSize={(d: any) => nodeSize(d.data?.conversions ?? 0)}
        enableGridX={false}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          legend: 'Spend',
          legendPosition: 'middle' as const,
          legendOffset: 40,
          format: (v: number) => formatCurrency(v),
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          legend: 'ROAS',
          legendPosition: 'middle' as const,
          legendOffset: -55,
          format: (v: number) => `${v}x`,
        }}
        motionConfig="gentle"
        layers={['grid', 'axes', BreakEvenLayer, 'nodes', 'markers', 'mesh', 'legends', 'annotations']}
        tooltip={({ node }) => {
          const d = node.data as any
          return (
            <div className="bg-popover rounded-lg shadow-lg px-3 py-2 text-sm border border-border">
              <p className="font-semibold text-popover-foreground">{d.campaignName}</p>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: node.color }} />
                <span className="text-muted-foreground">Spend:</span>
                <span className="font-medium">{formatCurrency(d.x)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: node.color }} />
                <span className="text-muted-foreground">ROAS:</span>
                <span className="font-medium">{Number(d.y).toFixed(1)}x</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: node.color }} />
                <span className="text-muted-foreground">Conversions:</span>
                <span className="font-medium">{d.conversions.toLocaleString()}</span>
              </div>
            </div>
          )
        }}
        legends={Object.keys(statusGroups).length > 1 ? [{
          anchor: 'top-right' as const,
          direction: 'column' as const,
          translateX: 0,
          translateY: 0,
          itemsSpacing: 4,
          itemWidth: 80,
          itemHeight: 16,
          symbolSize: 10,
          symbolShape: 'circle',
        }] : []}
      />
    </div>
  )
}
